import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'glass' | 'liquid-glass';
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  variant = 'default',
  className = '',
  ...props
}) => {
  const baseInputStyles = `
    w-full rounded-lg border px-3 py-2.5 text-sm shadow-sm transition-colors
    placeholder:text-theme-tertiary focus:outline-none focus:ring-2 focus:ring-offset-0
  `;

  const variantStyles = {
    default: `
      bg-surface text-theme
      ${error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200 dark:border-red-700 dark:focus:ring-red-900/50'
        : 'border-theme focus:border-primary-500 focus:ring-primary-100 dark:focus:ring-primary-900/50'}
    `,
    glass: `
      glass glass-border text-theme
      ${error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
        : 'focus:border-primary-500 focus:ring-primary-100'}
    `,
    'liquid-glass': `
      glass-input text-white placeholder:text-white/40
      ${error
        ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/30'
        : ''}
    `,
  };

  const disabledStyles = props.disabled ? 'opacity-60 cursor-not-allowed bg-surface-secondary' : '';

  const labelStyles = variant === 'liquid-glass'
    ? 'block text-sm font-medium text-white/70 mb-1.5'
    : 'block text-sm font-medium text-theme-secondary mb-1.5';

  const iconStyles = variant === 'liquid-glass'
    ? 'absolute left-3 top-2.5 text-white/50 pointer-events-none'
    : 'absolute left-3 top-2.5 text-theme-tertiary pointer-events-none';

  const errorStyles = variant === 'liquid-glass'
    ? 'mt-1 text-xs text-red-300'
    : 'mt-1 text-xs text-red-500 dark:text-red-400';

  return (
    <div className="w-full">
      {label && (
        <label className={labelStyles}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          className={`
            ${baseInputStyles}
            ${variantStyles[variant]}
            ${icon ? 'pl-10' : ''}
            ${disabledStyles}
            ${className}
          `}
          {...props}
        />
        {icon && (
          <div className={iconStyles}>
            {icon}
          </div>
        )}
      </div>
      {error && (
        <p className={errorStyles}>{error}</p>
      )}
    </div>
  );
};
